/**
 * src/lib/contractFinancials.js
 * ─────────────────────────────────────────────────────────────────────────
 * SHARED DOMAIN BUSINESS LOGIC — CostSpace
 *
 * Purpose (general, NOT tied to any single screen or report):
 *   Computes the financial position of one or more "contracts" (subcontracts
 *   or equipment contracts) from raw certification, retention-release, and
 *   cash-payment records.
 *
 *   This is domain logic, not report logic and not screen logic. Any current
 *   or future screen/report/widget that needs "how much is certified /
 *   retained / paid / outstanding for a contract" should call this module
 *   instead of re-deriving the calculation locally.
 *
 * Design constraints (strict):
 *   - 100% pure functions. No Supabase import, no React state, no Context,
 *     no Date.now(), no other global/ambient state.
 *   - Deterministic: same inputs always produce the same outputs.
 *   - All data access (fetching contracts/ipcs/releases/payments) remains the
 *     responsibility of the caller (screen or report). This module only
 *     computes from already-fetched arrays.
 *
 * Architectural note — relationship to buildCostControlData.js:
 *   This project's existing shared file, src/lib/buildCostControlData.js,
 *   mixes data-fetching (Supabase calls) with computation inside the same
 *   async function. This module intentionally follows a stricter pattern
 *   (pure computation only, no I/O) as the reference pattern for new shared
 *   business logic going forward. buildCostControlData.js is not being
 *   retrofitted to this pattern in this phase — this is a deliberate,
 *   documented difference in style between an older and a newer shared
 *   module, not an inconsistency to "fix" incidentally.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * KNOWN TECHNICAL DEBT (recorded, NOT solved here — out of scope for Stage 1)
 * ─────────────────────────────────────────────────────────────────────────
 * TD-1: buildCostControlData.js mixes fetch + compute in one async function.
 *       A future task could split it into a fetch layer + a pure compute
 *       layer, mirroring the pattern used here. Not done in this phase.
 *
 * TD-2: The `fetchAllRows()` pagination helper (documented project-wide
 *       convention for tables that can exceed 1,000 rows) is currently
 *       duplicated locally inside CashOutForecast.jsx and Finance.jsx instead
 *       of living in a shared /lib file. Not addressed here — this module has
 *       no pagination concerns since it receives already-fetched arrays.
 *
 * TD-3: Number-formatting helpers (`fmt`, `fmtM`) are duplicated across ~23
 *       files project-wide. Out of scope (presentation concern, not domain
 *       logic) — noted here only for future cleanup tracking.
 *
 * TD-4 (discovered while designing this module): Prior to this module, the
 *       "match cash payments to a contract via po_id" calculation existed
 *       independently in THREE places:
 *         1. Subcontracts.jsx  (Financial Summary tab — sums ALL payment
 *                                categories per contract via po_id)
 *         2. IPC.jsx           (`paidByContract` — sums ALL payment
 *                                categories per contract via po_id, same as
 *                                (1). Separately, and unrelated to the total,
 *                                it also tracks the `advanced_payment`
 *                                category alone in a distinct map,
 *                                `eqAdvPayByContract`, for its own purpose —
 *                                this is NOT a category exclusion from the
 *                                paid total, both totals are computed the
 *                                same way in (1) and (2))
 *         3. SubcontractReport.jsx (printed report — separate query entirely,
 *                                and does NOT subtract retention_releases at all)
 *       This module's `matchPaymentsToContracts()` unifies case (1) and (2)
 *       exactly as they currently behave (no category filtering needed).
 *       Case (3)'s missing retention-release subtraction is the Critical bug
 *       documented in the Phase 2 audit; consuming this module going forward
 *       fixes it structurally rather than patching the symptom.
 *       IPC.jsx's separate `eqAdvPayByContract` tracking is a distinct
 *       calculation (advance payment alone) and is out of scope for this
 *       module; it is not something this function needs to reproduce.
 * ─────────────────────────────────────────────────────────────────────────
 */

/**
 * Builds a lookup of contract_id -> total cash paid, by matching
 * cf_payment_entries rows to contracts via po_id.
 *
 * Sums ALL payment categories per contract — this matches the current
 * behavior of both Subcontracts.jsx's Financial Summary tab and IPC.jsx's
 * `paidByContract`. No category filtering is applied (see TD-4 above for why
 * this module intentionally does not add a category-exclusion option: no
 * current caller filters the paid total by category).
 *
 * @param {Array<{id: string|number, po_id: string|number|null}>} contracts
 * @param {Array<{po_id: string|number|null, amount_inc_vat: number|string, category?: string}>} paymentEntries
 *
 * @returns {Map<string|number, number>} contractId -> totalPaid
 */
export function matchPaymentsToContracts(contracts, paymentEntries) {
  const poToContract = new Map()
  for (const c of (contracts || [])) {
    if (c.po_id != null) poToContract.set(c.po_id, c.id)
  }

  const paidMap = new Map()
  for (const p of (paymentEntries || [])) {
    if (p.po_id == null) continue
    const contractId = poToContract.get(p.po_id)
    if (contractId == null) continue
    const amt = parseFloat(p.amount_inc_vat) || 0
    paidMap.set(contractId, (paidMap.get(contractId) || 0) + amt)
  }
  return paidMap
}

/**
 * Builds a lookup of contract_id -> retention held (gross retention from
 * approved/paid IPCs, minus any retention released back to the contractor).
 *
 * @param {Array<{subcontract_id?: string|number|null, equipment_contract_id?: string|number|null, retention_amount: number|string, status?: string}>} ipcs
 * @param {Array<{subcontract_id?: string|number|null, equipment_contract_id?: string|number|null, amount: number|string}>} retentionReleases
 * @param {{ statusFilter?: string[] }} [options]
 *   statusFilter: IPC statuses to include when summing retention (default: ['approved','paid'])
 *
 * @returns {Map<string|number, {gross: number, released: number, held: number}>}
 */
export function computeRetentionHeld(ipcs, retentionReleases, options = {}) {
  const { statusFilter = ['approved', 'paid'] } = options

  const contractIdOf = (row) => row.subcontract_id ?? row.equipment_contract_id ?? null

  const grossMap = new Map()
  for (const i of (ipcs || [])) {
    if (statusFilter.length && !statusFilter.includes(i.status)) continue
    const contractId = contractIdOf(i)
    if (contractId == null) continue
    const amt = parseFloat(i.retention_amount) || 0
    grossMap.set(contractId, (grossMap.get(contractId) || 0) + amt)
  }

  const releasedMap = new Map()
  for (const r of (retentionReleases || [])) {
    const contractId = contractIdOf(r)
    if (contractId == null) continue
    const amt = parseFloat(r.amount) || 0
    releasedMap.set(contractId, (releasedMap.get(contractId) || 0) + amt)
  }

  const result = new Map()
  const allContractIds = new Set([...grossMap.keys(), ...releasedMap.keys()])
  for (const contractId of allContractIds) {
    const gross = grossMap.get(contractId) || 0
    const released = releasedMap.get(contractId) || 0
    const held = Math.max(0, gross - released)
    result.set(contractId, { gross, released, held })
  }
  return result
}

/**
 * Computes the full financial position for a set of contracts
 * (subcontracts and/or equipment contracts).
 *
 * @param {Array<{
 *   id: string|number,
 *   contract_no?: string,
 *   contractor_name?: string,
 *   contract_value: number|string,
 *   po_id?: string|number|null,
 *   contract_type: 'material'|'equipment'|string
 * }>} contracts
 * @param {Array<{
 *   subcontract_id?: string|number|null,
 *   equipment_contract_id?: string|number|null,
 *   gross_amount: number|string,
 *   retention_amount: number|string,
 *   net_payable: number|string,
 *   status?: string
 * }>} ipcs
 * @param {Array<{
 *   subcontract_id?: string|number|null,
 *   equipment_contract_id?: string|number|null,
 *   amount: number|string
 * }>} retentionReleases
 * @param {Array<{
 *   po_id?: string|number|null,
 *   amount_inc_vat: number|string,
 *   category?: string
 * }>} paymentEntries
 * @param {{
 *   statusFilter?: string[],
 *   onlyWithActivity?: boolean
 * }} [options]
 *   statusFilter: IPC statuses to include (default: ['approved','paid'])
 *   onlyWithActivity: if true, drop contracts with zero certified AND zero paid
 *                     (mirrors Subcontracts.jsx's current Financial Summary
 *                     filter — default: false, since this is a general-purpose
 *                     function and callers should opt in explicitly)
 *
 * @returns {Array<{
 *   id: string|number,
 *   contract_type: string,
 *   contract_no: string,
 *   contractor_name: string,
 *   contract_value: number,
 *   po_id: string|number|null,
 *   cumulative_certified: number,
 *   cumulative_net_payable: number,
 *   retention_total: number,
 *   retention_released: number,
 *   retention_held: number,
 *   total_paid: number,
 *   outstanding: number
 * }>}
 */
export function computeContractFinancials(contracts, ipcs, retentionReleases, paymentEntries, options = {}) {
  const {
    statusFilter = ['approved', 'paid'],
    onlyWithActivity = false,
  } = options

  const contractIdOf = (row) => row.subcontract_id ?? row.equipment_contract_id ?? null

  // Group IPCs by contract for certified/net-payable totals
  const certifiedMap = new Map()
  const netPayableMap = new Map()
  for (const i of (ipcs || [])) {
    if (statusFilter.length && !statusFilter.includes(i.status)) continue
    const contractId = contractIdOf(i)
    if (contractId == null) continue
    certifiedMap.set(contractId, (certifiedMap.get(contractId) || 0) + (parseFloat(i.gross_amount) || 0))
    netPayableMap.set(contractId, (netPayableMap.get(contractId) || 0) + (parseFloat(i.net_payable) || 0))
  }

  const retentionMap = computeRetentionHeld(ipcs, retentionReleases, { statusFilter })
  const paidMap = matchPaymentsToContracts(contracts, paymentEntries)

  const rows = (contracts || []).map((c) => {
    const cumulativeCertified = certifiedMap.get(c.id) || 0
    const cumulativeNetPayable = netPayableMap.get(c.id) || 0
    const retention = retentionMap.get(c.id) || { gross: 0, released: 0, held: 0 }
    const totalPaid = paidMap.get(c.id) || 0
    const outstanding = cumulativeNetPayable - totalPaid

    return {
      id: c.id,
      contract_type: c.contract_type,
      contract_no: c.contract_no || '',
      contractor_name: c.contractor_name || '',
      contract_value: parseFloat(c.contract_value) || 0,
      po_id: c.po_id ?? null,
      cumulative_certified: cumulativeCertified,
      cumulative_net_payable: cumulativeNetPayable,
      retention_total: retention.gross,
      retention_released: retention.released,
      retention_held: retention.held,
      total_paid: totalPaid,
      outstanding,
    }
  })

  if (onlyWithActivity) {
    return rows.filter((r) => r.cumulative_certified > 0 || r.total_paid > 0)
  }
  return rows
}
